// Related Sentry docs: https://docs.sentry.io/platforms/javascript/tracing/instrumentation/custom-instrumentation/
import type { Span } from "../sentry"
import type { MeasureOptions, PerformanceEntry } from "./index.d"

let Sentry: Promise<(typeof import("../sentry"))["Sentry"]>
const getSentry = () => (Sentry ??= import("../sentry").then((m) => m.Sentry))

export const mark: PerformanceEntry["mark"] = (name: string) => createMark()(name)

/** @returns `mark` function creating a new performance span */
const createMark =
  (parent?: Promise<Span>) =>
  (name: string, startTime = now()) => {
    const span = Promise.all([getSentry(), parent]).then(([Sentry, parentSpan]) =>
      Sentry.startInactiveSpan({ name, startTime, parentSpan }),
    )

    return {
      measure: createMeasure(span),
      child: createChild(span),
    }
  }

/** @returns `measure` function completing the current performance span */
const createMeasure = (span: Promise<Span>) => (options?: MeasureOptions & { endTime: number }) => {
  const endTimestamp = options?.endTime ?? now()

  span.then(async (span) => {
    if (options?.detail) {
      span.setAttributes(options.detail)
    }

    if (options?.children) {
      const children = options.children
      getSentry()
        .then((Sentry) => populateChildSpans(Sentry, span, children))
        .then(() => span.end(endTimestamp))
    } else {
      span.end(endTimestamp)
    }
  })
}

/** @returns function for creation `child` performance spans */
const createChild = (span: Promise<Span>) => () => ({
  mark: createMark(span),
})

/** Current time in seconds */
const now = () => Date.now() / 1000

/** @internal */
const populateChildSpans = (
  Sentry: (typeof import("../sentry"))["Sentry"],
  parentSpan: Span,
  children: NonNullable<MeasureOptions["children"]>,
) => {
  for (const { name, startTime, duration, children: nestedChildren, detail } of children) {
    const span = Sentry.startInactiveSpan({ name, startTime, parentSpan })

    if (detail) span.setAttributes(detail)
    if (nestedChildren) populateChildSpans(Sentry, span, nestedChildren)

    span.end(startTime + duration)
  }
}
